import { type AssetClass, PdpScoreStatus, type ScoreStatus } from '@vizit/api';
import { Badge, BadgeLevel, BadgeShape, BadgeSize, EditedBadge, ScorePill, Skeleton, Tooltip } from '@vizit/atoms';
import { ScoreStickerV2, ScoreStickerV2Size, Text, TextColor, TextSize, TextVariant } from '@vizit/atoms';
import { TooltipTrigger } from '@vizit/atoms';
import { FEATURE_FLAGS, NEEDS_CATEGORY_SCORE_LABEL, ScoreDescriptions } from '@vizit/constants';
import { UNIVERSAL_BENCHMARK_NAME, universalCategoryBreadcrumbs } from '@vizit/constants';
import { useFeatureFlag } from '@vizit/feature-flags';
import { UsFlagCircleIcon } from '@vizit/icons';
import { ScoredAgainstBox, type ScoredAgainstBoxProps } from '@vizit/molecules';
import { cx } from '@vizit/theme';
import { Focusable } from 'react-aria-components';
import { AssetClassDropdown } from '../asset-class-dropdown';
import { CategoryBreadcrumbs } from '../category-breadcrumbs';

interface ScoreHeaderProps {
  /**
   * The score of the asset
   */
  score?: number | null;
  /**
   * The score status of the asset
   */
  scoreStatus: ScoreStatus | null;
  /**
   * Whether the score is omitted
   */
  omitScore?: boolean | null;
  /**
   * The score settled without one for a reason retrying won't fix, such as an image the benchmark pair did not score.
   * Reads "Unscored" in place of the cannot-score error; no effect while the score loads, is present or is omitted.
   * @default false
   */
  isUnscored?: boolean;
  /**
   * The category of the asset
   */
  category: string | null;
  /**
   * The category breadcrumbs from L1 down to the leaf category, shown in a tooltip on the category value
   */
  categoryBreadcrumbs?: string[];
  /**
   * Whether the category is a universal one: a US flag before it and the benchmark name leading its breadcrumbs
   * @default false
   */
  isUniversalCategory?: boolean;
  /**
   * The type of the asset| null;
  /**
   * The type of the asset
   */
  assetType: 'hero' | 'carousel' | null;
  /**
   * The classification label (shown when feature flag is enabled)
   */
  classification?: AssetClass | null;
  /**
   * Whether the asset's class is editable (server-side eligibility flag from `is_class_editable`).
   * Drives the pencil affordance — pencil only renders when this is true AND
   * both `FEAT_CHANGE_ASSET_CLASS` and `SHOW_IMAGE_CLASSIFICATION` feature flags
   * are on (so the classification text the pencil edits is actually visible).
   */
  isClassEditable?: boolean;
  /**
   * Whether the asset's class has been edited by a user.
   * Renders the EDITED badge inline next to the classification text when this is
   * true AND both `FEAT_CHANGE_ASSET_CLASS` and `SHOW_IMAGE_CLASSIFICATION` are on.
   */
  classEdited?: boolean;
  /**
   * Callback fired when the user picks a new class from the dropdown.
   * Wired to the real mutation in `useChangeAssetClass` at the consumer site.
   */
  onClassChange?: (next: AssetClass) => void;
  /**
   * The original (ML) classification, forwarded to the dropdown's reset footer.
   */
  originalClassification?: AssetClass | null;
  /**
   * Fired when the user clicks "Reset" in the dropdown footer.
   */
  onReset?: () => void;
  /**
   * Whether custom benchmarks are on for the asset's organisation: the flag and the API both
   * have them on. Hero images then always read "Hero", and `scoredAgainst` shows.
   * @default false
   */
  customBenchmarksEnabled?: boolean;
  /**
   * The benchmark and product category the score was taken against. Rendered as the
   * "Scored against" box in place of the Category column when custom benchmarks are on;
   * `null` renders nothing extra.
   */
  scoredAgainst?: Omit<ScoredAgainstBoxProps, 'className'> | null;
  /**
   * The benchmark in `scoredAgainst` has no product category to score against yet: no score, and a prompt to pick one
   * in place of the cannot-score error. Only with custom benchmarks on.
   * @default false
   */
  isAwaitingCategory?: boolean;
  /**
   * The loading state of the asset score
   */
  isLoading: boolean;
}

interface ResolveClassLabelOptions {
  assetType: 'hero' | 'carousel' | null;
  classification?: AssetClass | null;
  showClassification: boolean;
  customBenchmarksEnabled: boolean;
}

/**
 * The text of the Class column. Hero images read "Hero" (always under custom benchmarks, and
 * whenever no classification shows otherwise). Carousel images read their classification when it
 * is shown and falls back to the capitalized asset type when it is not, under either flag.
 */
function resolveClassLabel(options: ResolveClassLabelOptions): string | null | undefined {
  const { assetType, classification, showClassification, customBenchmarksEnabled } = options;
  const capitalizedType = assetType ? assetType.charAt(0).toUpperCase() + assetType.slice(1) : undefined;

  if (customBenchmarksEnabled && assetType === 'hero') return 'Hero';
  return showClassification ? (classification ?? capitalizedType) : capitalizedType;
}

function ScoreHeader(props: ScoreHeaderProps) {
  const {
    score = null,
    omitScore = false,
    isUnscored = false,
    scoreStatus,
    category,
    categoryBreadcrumbs,
    isUniversalCategory = false,
    assetType: assetTypeProp,
    classification,
    isClassEditable = false,
    classEdited = false,
    onClassChange,
    originalClassification,
    onReset,
    customBenchmarksEnabled = false,
    scoredAgainst = null,
    isAwaitingCategory = false,
    isLoading,
  } = props;

  const showClassification = useFeatureFlag(FEATURE_FLAGS.SHOW_IMAGE_CLASSIFICATION, false);
  const changeAssetClassEnabled = useFeatureFlag(FEATURE_FLAGS.FEAT_CHANGE_ASSET_CLASS, false);

  const isHero = assetTypeProp === 'hero';
  const displayedType = resolveClassLabel({
    assetType: assetTypeProp,
    classification,
    showClassification,
    customBenchmarksEnabled,
  });
  const showScoredAgainst = customBenchmarksEnabled && !!scoredAgainst;
  const scoreLabel = isHero ? 'Vizit Hero Score' : 'Vizit Image Score';
  const assetTypeLabel = isHero ? 'hero' : 'carousel';

  const isScoreOmitted = !!omitScore;
  const isScoreAvailable = !isScoreOmitted && score !== null;
  const isScoreLoading =
    isLoading || (scoreStatus !== PdpScoreStatus.Scored && scoreStatus !== PdpScoreStatus.RefreshPending);
  const isScoreBroken = !isScoreAvailable && !isScoreOmitted && !isScoreLoading;
  const isScoreUnscored = isScoreBroken && isUnscored;
  const isScoreAwaitingCategory = isScoreBroken && customBenchmarksEnabled && isAwaitingCategory;

  const canEditClass = isClassEditable && changeAssetClassEnabled && showClassification;
  const showEditedBadge = classEdited && changeAssetClassEnabled && showClassification;

  const grayBadgeProps = {
    level: BadgeLevel.Default,
    shape: BadgeShape.Box,
    size: BadgeSize.Sm,
    bordered: false,
    className: 'self-start bg-gray-utility-200 font-medium',
  } as const;

  const labelClasses = 'leading-[var(--heading-height)]';
  const labelProps = { variant: TextVariant.XsSemibold, color: TextColor.Tertiary, role: 'heading' } as const;
  const classValueClasses = cx('leading-[var(--text-height)]', !displayedType && 'text-center text-tertiary');

  const hasBreadcrumbs = !!category && !!categoryBreadcrumbs?.length;
  const categoryValueClasses = cx(
    'leading-[var(--text-height)]',
    !category && 'text-center text-tertiary',
    // A faint dotted underline hints that the value has a tooltip
    hasBreadcrumbs && 'underline decoration-dotted decoration-gray-utility-300 underline-offset-2',
  );
  const categoryText = (
    <Text variant={TextVariant.XsRegular} className={categoryValueClasses}>
      {category ?? '-'}
    </Text>
  );
  const categoryValue =
    isUniversalCategory && category ? (
      <span className="flex min-w-0 items-center gap-3">
        <span role="img" aria-label={UNIVERSAL_BENCHMARK_NAME} className="flex shrink-0 items-center">
          <UsFlagCircleIcon size={16} />
        </span>
        {categoryText}
      </span>
    ) : (
      categoryText
    );
  const tooltipBreadcrumbs = isUniversalCategory
    ? universalCategoryBreadcrumbs(categoryBreadcrumbs ?? [])
    : (categoryBreadcrumbs ?? []);

  return (
    <div className="h-44 w-full flex items-center max-md:h-full">
      <div className="flex flex-row gap-16 px-16 py-2 [--heading-height:24px] [--text-height:20px] max-lg:px-8 max-lg:py-2 max-md:gap-7">
        <div className="flex flex-row gap-8 items-center max-md:gap-7">
          <ScoreStickerV2
            score={isScoreAvailable ? score : null}
            size={ScoreStickerV2Size.Sm}
            isLoading={!isScoreOmitted && isScoreLoading}
            isInformational={isScoreOmitted}
          />
          <div className="flex flex-col gap-2 w-full max-md:hidden">
            {isScoreOmitted ? (
              <>
                <Badge {...grayBadgeProps}>{ScoreDescriptions.Unscored}</Badge>
                <Text maxLines={2} size={TextSize.Xs} className="leading-[var(--text-height)]">
                  Verified for presence, accuracy, and placement in listing. Not scored on visual power to convert.
                </Text>
              </>
            ) : (
              <>
                <div className="flex flex-row gap-4 items-start max-md:flex-wrap">
                  <Text {...labelProps} elementType="p" className={labelClasses}>
                    {scoreLabel}
                  </Text>
                  {isScoreAwaitingCategory ? (
                    <Badge {...grayBadgeProps}>{NEEDS_CATEGORY_SCORE_LABEL}</Badge>
                  ) : isScoreUnscored ? (
                    <Badge {...grayBadgeProps}>Unscored</Badge>
                  ) : (
                    <ScorePill score={isScoreAvailable ? score : null} isLoading={isScoreLoading} />
                  )}
                </div>
                {isScoreLoading && <Skeleton className="w-[408px] inline-block h-[var(--text-height)]" />}
                {isScoreAvailable && (
                  <Text maxLines={2} size={TextSize.Xs} className="leading-[var(--text-height)]">
                    This image is likely to <span className="font-medium">convert better than {score}%</span> of other
                    <span className="ml-1">{assetTypeLabel}</span> images in the same category.
                  </Text>
                )}
                {isScoreBroken && (
                  <Text maxLines={2} size={TextSize.Xs} className="leading-[var(--text-height)]">
                    {isScoreAwaitingCategory
                      ? 'Pick a category on the collection to score this image against this benchmark.'
                      : isScoreUnscored
                        ? 'This image was not scored against this benchmark.'
                        : 'Cannot score this image at this time. Please try again later.'}
                  </Text>
                )}
              </>
            )}
          </div>
        </div>
        <div className="flex flex-row gap-16 self-center max-md:gap-7">
          <div className="flex flex-col gap-2 items-start">
            {/* Pin the row to the heading height so the EDITED badge (taller than
                the label text) can't push the value down — keeps the value's
                spacing below the header identical to the Category column. */}
            <div className="flex flex-row gap-2 items-center h-[var(--heading-height)]">
              <Text {...labelProps} elementType="p" className={labelClasses}>
                Class
              </Text>
              {showEditedBadge && <EditedBadge />}
            </div>
            {isLoading ? (
              <Skeleton className="w-full h-[var(--text-height)]" />
            ) : (
              // Pin to the text height: the edit-pencil button is taller than the
              // class text, so without this the row grows to the button and
              // `items-center` shoves the text down — breaking alignment with the
              // Category column. The pencil stays visually centered on the text.
              <div className="flex flex-row gap-2 items-center h-[var(--text-height)]">
                <Text variant={TextVariant.XsRegular} className={classValueClasses}>
                  {displayedType ?? '-'}
                </Text>
                {canEditClass && (
                  <AssetClassDropdown
                    value={classification ?? null}
                    onChange={onClassChange ?? (() => {})}
                    originalClassification={originalClassification}
                    onReset={onReset}
                  />
                )}
              </div>
            )}
          </div>
          {showScoredAgainst ? (
            <ScoredAgainstBox benchmark={scoredAgainst.benchmark} productCategory={scoredAgainst.productCategory} />
          ) : (
            <div className="flex flex-col gap-2 items-start">
              <Text {...labelProps} elementType="p" className={labelClasses}>
                Category
              </Text>
              {isLoading ? (
                <Skeleton className="w-full h-[var(--text-height)]" />
              ) : hasBreadcrumbs ? (
                <TooltipTrigger delay={200}>
                  {/* The value is a plain span; Focusable lets it drive the tooltip and keeps it keyboard-reachable */}
                  <Focusable>
                    <span
                      // biome-ignore lint/a11y/noNoninteractiveTabindex: the value must be keyboard-reachable to open its tooltip
                      tabIndex={0}
                      className="flex min-w-0 cursor-default rounded-xs outline-none focus-visible:ring-2 focus-visible:ring-gray-modern-500"
                    >
                      {categoryValue}
                    </span>
                  </Focusable>
                  <Tooltip placement="bottom">
                    <CategoryBreadcrumbs levels={tooltipBreadcrumbs} />
                  </Tooltip>
                </TooltipTrigger>
              ) : (
                categoryValue
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export { ScoreHeader };
